import React from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import FadeInImage from "@/components/FadeInImage";
import { cn } from "@/lib/utils";
import useFormat from "@/hooks/useFormat";

const SeriesThumbnailVariants = cva("aspect-[3/4]", {
  variants: {
    size: {
      xs: "rounded-sm",
      sm: "rounded-sm",
      default: "rounded",
      md: "rounded-md",
      lg: "rounded-lg",
    },
  },
});

const imageExtensions = ["avif", "webp", "jpg", "png"];

const SeriesThumbnail: React.FC<
  React.ComponentProps<"span"> & {
    thumbnail: string;
    thumbnailHeights: { mobile: string; desktop: string };
    // Localized `show.displayTitle`. This image is the whole content of the
    // cover link, so its `alt` is the link's accessible name (WCAG 2.4.4) —
    // a generic label would make every cover of a rail announce the same.
    alt: string;
    priority?: boolean;
  } & VariantProps<typeof SeriesThumbnailVariants>
> = ({
  className,
  thumbnail,
  thumbnailHeights,
  alt,
  size = "default",
  priority = false,
  ...props
}) => {
  const classes = SeriesThumbnailVariants({ size, className });
  const { getResizedThumbnailUrl } = useFormat();

  return (
    // The placeholder colour is on the root, not the img: the img fades in.
    // Spans, not divs: a cover sits inside the locked episode tile's button,
    // which only takes phrasing content. `block` is on the root alone, the
    // img shares `classes` and must stay inline.
    <span className={cn("bg-neutral-60 block", classes, className)} {...props}>
      <picture>
        {imageExtensions.map((extension) => (
          <React.Fragment key={extension}>
            <source
              srcSet={getResizedThumbnailUrl(
                thumbnail,
                thumbnailHeights.mobile,
                extension,
              )}
              media="(max-width: 1023px)"
              type={`image/${extension}`}
            />
            <source
              srcSet={getResizedThumbnailUrl(
                thumbnail,
                thumbnailHeights.desktop,
                extension,
              )}
              media={`(min-width: 1023px)`}
              type={`image/${extension}`}
            />
          </React.Fragment>
        ))}
        <FadeInImage
          src={getResizedThumbnailUrl(thumbnail, "1080")}
          alt={alt}
          fetchPriority={priority ? "high" : "auto"}
          loading={priority ? "eager" : "lazy"}
          className={cn("h-full w-full object-cover", classes)}
        />
      </picture>
      {/* The ring lightens when an ancestor marks `group/cover` (SeriesCover,
          MyListCard); a caller with none stays still. */}
      <span
        className={cn(
          "pointer-events-none relative top-0 left-0 z-10 block h-full w-full -translate-y-full inset-shadow-[0_0_0_1px] inset-shadow-white/20 transition-shadow duration-300 ease-out group-hover/cover:inset-shadow-white/40",
          classes,
        )}
      />
    </span>
  );
};

export default SeriesThumbnail;
